import React, { useState, useEffect, useContext, useCallback } from "react";
import DOMPurify from "dompurify";
import useScrollToTop from "../hooks/useScrollToTop";
import { getAboutInfo } from "../services/aboutServices";
import { LanguageContext } from "../contexts/LanguageContext";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const isExternalURL = (url) => {
  try {
    const newUrl = new URL(url);
    return newUrl.protocol === "http:" || newUrl.protocol === "https:";
  } catch {
    return false;
  }
};

const absUrl = (u) => (u ? (isExternalURL(u) ? u : `${BASE_URL}${u}`) : "");

const AboutPage = () => {
  useScrollToTop();

  const [aboutInfo, setAboutInfo] = useState(null);

  // Modal image (on garde un système "générique" avec nav si tu ajoutes plusieurs images plus tard)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImages, setModalImages] = useState([]);
  const [modalIndex, setModalIndex] = useState(0);

  const { language } = useContext(LanguageContext);

  // Helpers i18n
  const getLocalizedKey = useCallback(
    (key) => `${key}_${String(language || "FR").toLowerCase()}`,
    [language],
  );

  const getFallbackMessage = useCallback(
    (type) => {
      if (language === "FR")
        return type === "title" ? "Titre indisponible" : "Texte indisponible";
      return type === "title" ? "Title unavailable" : "Text unavailable";
    },
    [language],
  );

  // Fetch
  useEffect(() => {
    const fetchAboutInfo = async () => {
      try {
        const response = await getAboutInfo();
        setAboutInfo(response);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des informations :",
          error,
        );
      }
    };
    fetchAboutInfo();
  }, []);

  // Modal controls
  const openModal = useCallback((images, startIndex = 0) => {
    const arr = (images || []).filter(Boolean);
    if (!arr.length) return;

    setModalImages(arr);
    setModalIndex(Math.max(0, Math.min(startIndex, arr.length - 1)));
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setModalImages([]);
    setModalIndex(0);
  }, []);

  const hasNav = modalImages.length > 1;

  const prevImage = useCallback(() => {
    if (!hasNav) return;
    setModalIndex((i) => (i - 1 + modalImages.length) % modalImages.length);
  }, [hasNav, modalImages.length]);

  const nextImage = useCallback(() => {
    if (!hasNav) return;
    setModalIndex((i) => (i + 1) % modalImages.length);
  }, [hasNav, modalImages.length]);

  // Keyboard + scroll lock modal
  useEffect(() => {
    if (!isModalOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeModal();
      if (e.key === "ArrowLeft") prevImage();
      if (e.key === "ArrowRight") nextImage();
    };

    window.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [isModalOpen, closeModal, prevImage, nextImage]);

  if (!aboutInfo) {
    return (
      <p className="text-center text-gray-600 dark:text-gray-300 py-12">
        {language === "FR" ? "Chargement des données..." : "Loading data..."}
      </p>
    );
  }

  // DATA
  const title =
    aboutInfo[getLocalizedKey("parcours_title")] || getFallbackMessage("title");

  const textHtml =
    aboutInfo[getLocalizedKey("parcours_text")] || getFallbackMessage("text");

  const imageUrl = absUrl(aboutInfo.parcours_image);

  // Styles cohérents
  const sectionWrap =
    "relative z-10 max-w-[980px] mx-auto px-4 sm:px-6 lg:px-8";
  const softCard =
    "rounded-3xl border border-black/10 dark:border-white/10 " +
    "bg-[#E8BB89]/80 dark:bg-white/5 shadow-sm backdrop-blur-sm overflow-hidden";
  const softText = "text-gray-900 dark:text-white";
  const mutedText = "text-gray-800 dark:text-gray-300";

  return (
    <div className="relative min-h-screen pb-12">
      {/* Décor doux */}
      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute top-[60px] left-0 w-full h-[34vh] bg-cover bg-no-repeat bg-bottom opacity-60 dark:opacity-25"
          style={{ backgroundImage: "url('/images/fond_sakura.png')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/35 to-transparent dark:from-black/60" />
      </div>

      {/* HERO */}
      <section className={`${sectionWrap} pt-24 sm:pt-28`}>
        <div className="text-center">
          <div
            className="
              inline-flex items-center gap-2 rounded-full px-4 py-2
              bg-black/5 dark:bg-white/10
              border border-black/10 dark:border-white/10
              backdrop-blur-sm
            "
          >
            <span className="h-2 w-2 rounded-full bg-primary" />
            <span className="text-xs sm:text-sm font-satoshi font-semibold tracking-wide text-gray-700 dark:text-gray-200">
              {language === "FR" ? "Mon parcours" : "My journey"}
            </span>
          </div>

          <h1
            className={`mt-4 text-3xl sm:text-4xl lg:text-5xl font-satoshi font-extrabold tracking-tight ${softText}`}
          >
            {title}
          </h1>

          <div className="mt-4 flex justify-center">
            <div className="h-[3px] w-24 sm:w-32 rounded-full bg-primary/90" />
          </div>
        </div>
      </section>

      {/* IMAGE EN HAUT + TEXTE EN BLOC */}
      <section className={`${sectionWrap} mt-8`}>
        <div className={softCard}>
          {/* Image header */}
          {imageUrl && (
            <button
              type="button"
              onClick={() => openModal([imageUrl], 0)}
              className="
                group relative w-full text-left
                focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
              "
              aria-label={language === "FR" ? "Agrandir l'image" : "Open image"}
            >
              {/* cadre strict */}
              <div className="relative w-full aspect-[16/9] sm:aspect-[21/9] overflow-hidden bg-black/5 dark:bg-white/5">
                <img
                  src={imageUrl}
                  alt={language === "FR" ? "Mon parcours" : "My journey"}
                  className="
      h-full w-full object-contain
      transition-opacity duration-300
      group-hover:opacity-95
    "
                  loading="lazy"
                />

                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />

                <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3">
                  <div className="text-white">
                    <div className="text-sm font-semibold">
                      {language === "FR"
                        ? "Cliquer pour agrandir"
                        : "Click to enlarge"}
                    </div>
                    <div className="text-xs text-white/80">
                      {language === "FR"
                        ? "Aperçu de l’espace / pratique"
                        : "Preview of the place / practice"}
                    </div>
                  </div>

                  <div
                    className="
                      inline-flex items-center gap-2 rounded-2xl px-3 py-2
                      bg-white/15 backdrop-blur-md border border-white/15
                      text-white text-sm
                      transition group-hover:bg-white/20
                    "
                  >
                    <span className="text-base">⤢</span>
                    <span className="hidden sm:inline">
                      {language === "FR" ? "Agrandir" : "Open"}
                    </span>
                  </div>
                </div>
              </div>
            </button>
          )}

          {/* Body texte */}
          <div className="p-5 sm:p-7 lg:p-8">
            <div
              className={`
                prose prose-neutral max-w-none
                dark:prose-invert
                prose-headings:font-satoshi
                prose-p:leading-relaxed
                ${mutedText}
              `}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(textHtml),
              }}
            />
          </div>
        </div>
      </section>

      {/* Modal image */}
      {isModalOpen && modalImages[modalIndex] && (
        <div
          className="fixed inset-0 z-[999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="
              relative w-full max-w-5xl
              rounded-3xl overflow-hidden
              border border-white/10
              bg-black/35 shadow-2xl
            "
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-4 p-4 sm:p-5">
              <div className="text-white">
                <div className="font-semibold">
                  {language === "FR" ? "Agrandissement" : "Preview"}
                </div>
                {hasNav && (
                  <div className="text-white/70 text-xs">
                    {modalIndex + 1}/{modalImages.length}
                  </div>
                )}
              </div>

              <button
                className="
                  w-10 h-10 rounded-2xl
                  bg-white/10 hover:bg-white/15
                  border border-white/10
                  text-white transition
                "
                onClick={closeModal}
                aria-label={language === "FR" ? "Fermer" : "Close"}
                title={language === "FR" ? "Fermer" : "Close"}
              >
                ×
              </button>
            </div>

            <div className="relative flex items-center justify-center px-4 pb-5">
              {hasNav && (
                <button
                  type="button"
                  onClick={prevImage}
                  className="
                    absolute left-4 top-1/2 -translate-y-1/2
                    w-11 h-11 rounded-2xl
                    bg-white/10 hover:bg-white/15
                    border border-white/10
                    text-white transition
                  "
                  aria-label={
                    language === "FR" ? "Image précédente" : "Previous image"
                  }
                  title={language === "FR" ? "Précédent" : "Previous"}
                >
                  <FaChevronLeft className="mx-auto" />
                </button>
              )}

              <img
                src={modalImages[modalIndex]}
                alt="Agrandissement"
                className="max-h-[78vh] max-w-[92vw] object-contain rounded-2xl bg-black/20"
              />

              {hasNav && (
                <button
                  type="button"
                  onClick={nextImage}
                  className="
                    absolute right-4 top-1/2 -translate-y-1/2
                    w-11 h-11 rounded-2xl
                    bg-white/10 hover:bg-white/15
                    border border-white/10
                    text-white transition
                  "
                  aria-label={
                    language === "FR" ? "Image suivante" : "Next image"
                  }
                  title={language === "FR" ? "Suivant" : "Next"}
                >
                  <FaChevronRight className="mx-auto" />
                </button>
              )}
            </div>

            {hasNav && (
              <div className="px-5 pb-5 text-center text-xs text-white/70">
                {language === "FR"
                  ? "Astuce : ← → pour naviguer, Échap pour fermer."
                  : "Tip: use ← → to navigate, Esc to close."}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AboutPage;
